import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import type { CertificateSetId } from '../domain/certificateCollection';
import { claimDemoCertificate, claimDemoSetReward } from './demoCertificateCollection';
import {
  DEMO_STORAGE_KEY,
  LEGACY_DEMO_STORAGE_KEYS,
  completeDemoStage,
  createDemoSession,
  demoStages,
  normalizeDemoSession,
  sessionForPreset,
  type DemoFamilyExperience,
  type DemoMode,
  type DemoPreset,
  type DemoSession,
  type DemoStageId,
} from './demoExperience';

interface DemoContextValue {
  session: DemoSession;
  start(mode: DemoMode, preset?: DemoPreset, soundRequested?: boolean): void;
  setStage(index: number): void;
  completeStage(stageId: DemoStageId, selection?: string): void;
  setSelection(stageId: DemoStageId, selection: string): void;
  claimCertificate(certificateId: string): void;
  claimSetReward(setId: CertificateSetId): void;
  updateFamilyExperience(patch: Partial<DemoFamilyExperience>): void;
  next(): void;
  previous(): void;
  setAutoPlaying(autoPlaying: boolean): void;
  reset(): void;
}

const DemoContext = createContext<DemoContextValue | null>(null);

function loadSession(): DemoSession {
  if (typeof window === 'undefined') return createDemoSession();
  try {
    const raw = window.localStorage.getItem(DEMO_STORAGE_KEY);
    if (raw) return normalizeDemoSession(JSON.parse(raw));
    for (const legacyKey of LEGACY_DEMO_STORAGE_KEYS) {
      const legacyRaw = window.localStorage.getItem(legacyKey);
      if (legacyRaw) return normalizeDemoSession(JSON.parse(legacyRaw));
    }
    return createDemoSession();
  } catch {
    return createDemoSession();
  }
}

export function DemoProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<DemoSession>(loadSession);

  useEffect(() => {
    try { window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(session)); } catch { /* storage can be disabled */ }
  }, [session]);

  const value = useMemo<DemoContextValue>(() => ({
    session,
    start: (mode: DemoMode, preset: DemoPreset = 'first', soundRequested: boolean = true) => setSession(sessionForPreset(preset, mode, soundRequested)),
    setStage: (index: number) => setSession((current: DemoSession) => ({ ...current, currentStage: Math.max(0, Math.min(demoStages.length - 1, index)), autoPlaying: false, updatedAt: new Date().toISOString() })),
    completeStage: (stageId: DemoStageId, selection?: string) => setSession((current: DemoSession) => completeDemoStage(current, stageId, selection)),
    setSelection: (stageId: DemoStageId, selection: string) => setSession((current: DemoSession) => ({ ...current, selections: { ...current.selections, [stageId]: selection }, updatedAt: new Date().toISOString() })),
    claimCertificate: (certificateId: string) => setSession((current: DemoSession) => claimDemoCertificate(current, certificateId)),
    claimSetReward: (setId: CertificateSetId) => setSession((current: DemoSession) => claimDemoSetReward(current, setId)),
    updateFamilyExperience: (patch: Partial<DemoFamilyExperience>) => setSession((current: DemoSession) => ({ ...current, familyExperience: { ...current.familyExperience, ...patch }, updatedAt: new Date().toISOString() })),
    next: () => setSession((current: DemoSession) => ({ ...current, currentStage: Math.min(demoStages.length - 1, current.currentStage + 1), updatedAt: new Date().toISOString() })),
    previous: () => setSession((current: DemoSession) => ({ ...current, currentStage: Math.max(0, current.currentStage - 1), autoPlaying: false, updatedAt: new Date().toISOString() })),
    setAutoPlaying: (autoPlaying: boolean) => setSession((current: DemoSession) => ({ ...current, autoPlaying, updatedAt: new Date().toISOString() })),
    reset: () => {
      try {
        window.localStorage.removeItem(DEMO_STORAGE_KEY);
        LEGACY_DEMO_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));
      } catch { /* storage can be disabled */ }
      setSession(createDemoSession());
    },
  }), [session]);

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

export function useDemo(): DemoContextValue {
  const context = useContext(DemoContext);
  if (!context) throw new Error('DemoProvider is required');
  return context;
}
